import React, { FC, memo } from 'react';
import { Text, View } from '@react-pdf/renderer';

import { BASE_FONT_SIZE, PDF_PRIMARY_COLOR } from '../pdfStyles';

/**
 * Reusable PDF section for structured data (Transferred from/to, Receiving Bank, etc.)
 *
 * Layout:
 *   Title  Name
 *   ─────────────────── (subtle line)
 *   Label:    Value
 *   Label:    Value
 */

type PdfSectionDetail = { label: string; value: string };

type PdfSectionProps = {
    /** Section title — rendered in blue bold */
    title: string;
    /** Optional name next to title (e.g. bank name) — rendered in bold */
    name?: string;
    /** Optional label:value detail rows */
    details?: PdfSectionDetail[];
    /** Label column width in px (default 70) */
    labelWidth?: number;
};

const PdfSection: FC<PdfSectionProps> = ({ title, name, details, labelWidth = 70 }) => (
    <View style={{ marginBottom: 16, marginHorizontal: 10 }}>
        <View
            style={{
                flexDirection: 'row',
                borderBottom: '0.5px solid #ccc',
                paddingBottom: 4,
                marginBottom: 6
            }}
        >
            <Text style={{ fontWeight: 'bold', fontSize: BASE_FONT_SIZE + 1, color: PDF_PRIMARY_COLOR }}>
                {title}
            </Text>
            {name ? (
                <Text style={{ fontWeight: 'bold', fontSize: BASE_FONT_SIZE + 1, marginLeft: 8 }}>
                    {name}
                </Text>
            ) : null}
        </View>
        {details && details.length > 0 && (
            <View style={{ paddingLeft: 4 }}>
                {details.map((item, idx) => (
                    <View key={idx} style={{ flexDirection: 'row', paddingVertical: 2 }}>
                        <Text style={{ width: labelWidth, fontSize: BASE_FONT_SIZE, color: '#777' }}>
                            {item.label}:
                        </Text>
                        <Text style={{ fontSize: BASE_FONT_SIZE }}>{item.value}</Text>
                    </View>
                ))}
            </View>
        )}
    </View>
);

/**
 * Remarks / note-style section for secondary contextual content.
 *
 * Visual: subtle left accent border + light background.
 * Hidden entirely when no content.
 */
type PdfRemarksSectionProps = {
    /** Remark text — if empty/null, section is hidden */
    body?: string | null;
    /** Optional title override (default: "Remarks") */
    title?: string;
};

const PdfRemarksSection: FC<PdfRemarksSectionProps> = ({ body, title = 'Remarks' }) => {
    if (!body) return null;

    return (
        <View style={{ marginTop: 8, marginBottom: 4, marginHorizontal: 10 }}>
            <Text
                style={{
                    fontSize: BASE_FONT_SIZE - 1,
                    color: '#6b7280',
                    fontWeight: 500,
                    marginBottom: 3
                }}
            >
                {title}
            </Text>

            <View
                style={{
                    borderLeftWidth: 2,
                    borderLeftColor: PDF_PRIMARY_COLOR,
                    backgroundColor: '#f9fafb',
                    paddingVertical: 6,
                    paddingHorizontal: 10,
                    borderRadius: 3
                }}
            >
                <Text
                    style={{
                        fontSize: BASE_FONT_SIZE,
                        color: '#1f2937',
                        lineHeight: 1.4
                    }}
                >
                    {body}
                </Text>
            </View>
        </View>
    );
};

export default memo(PdfSection);
export { PdfRemarksSection };
